import {
  AttachMoney,
  Calculate,
  Info as InfoIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { getDollarValue, getUfValue } from 'lib/api/cmf';
import { updateSGRWarrantyRequest } from 'lib/api/sgr';
import AlertBanner from 'lib/designSystem/AlertBanner';
import CustomButton from 'lib/designSystem/Button';
import CustomDialogTitle from 'lib/designSystem/DialogTitle';
import riskPremiumCalculator, {
  Output,
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { formatMoney, showAlert } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type CalculationResult = Output;

interface WarrantyEditConfirmationProps {
  open: boolean;
  formData: any;
  rateControls: RiskPremiumRates;
  subProductsData: any[];
  isApproved: boolean;
  originalWarranty: Warranty;
  onClose: () => void;
  refreshWarranty?: () => void;
  setIsEditing: (isEditing: boolean) => void;
}

const WarrantyEditConfirmation: React.FC<WarrantyEditConfirmationProps> = ({
  open,
  formData,
  rateControls,
  subProductsData,
  isApproved,
  originalWarranty,
  onClose,
  refreshWarranty,
  setIsEditing,
}) => {
  const dispatch = useDispatch();
  const { mutate, isPending } = updateSGRWarrantyRequest(dispatch);
  const { data: dollar } = getDollarValue(dispatch);
  const { data: uf } = getUfValue(dispatch);
  const { calculate } = riskPremiumCalculator();
  const [runDecisionEngine, setRunDecisionEngine] = useState(false);
  const [originalCalculation, setOriginalCalculation] =
    useState<CalculationResult | null>(null);
  const [modifiedCalculation, setModifiedCalculation] =
    useState<CalculationResult | null>(null);

  const { quotedAmount, quotedCurrency, startDate, endDate, type } = formData;

  const daysDifference = Math.ceil(
    (new Date(endDate).getTime() - new Date(startDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );
  const amount = Number(quotedAmount);

  const originalWarrantyAmount = originalWarranty.quotedAmount || 0;

  const handleConfirm = async () => {
    const conversionRates = { UF: uf?.value, USD: dollar?.value, CLP: 1 };
    const data = {
      ...formData,
      quotedAmount: Number(formData.quotedAmount),
      conversionRate:
        conversionRates[
          formData.quotedCurrency as keyof typeof conversionRates
        ],
      businessIdentifier: originalWarranty.businessIdentifier,
      operationCostFixed: rateControls.fixedRate,
      operationCostVariable: rateControls.variableRate,
      discountRateFixed: rateControls.fixedDiscount,
      discountRateVariable: rateControls.variableDiscount,
      description: formData.description,
      runDecisionEngine,
      deleteUnitCode: formData.principalRut && !formData.unitCode,
    };

    mutate(
      { warrantyRequestId: originalWarranty.id, data },
      {
        onSuccess: () => {
          onClose();
          setIsEditing(false);
          if (refreshWarranty) refreshWarranty();
          showAlert(dispatch, 'success', 'Garantía actualizada exitosamente');
        },
      },
    );
  };

  useEffect(() => {
    const performCalculations = () => {
      try {
        const original = calculate({
          quotedAmount: originalWarrantyAmount,
          startDate: originalWarranty.startDate,
          endDate: originalWarranty.endDate,
          quotedCurrency: originalWarranty.quotedCurrency,
          rates: originalRates,
        });

        const modified = calculate({
          quotedAmount: amount,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
          quotedCurrency,
          rates: rateControls,
        });

        setOriginalCalculation(original);
        setModifiedCalculation(modified);
      } catch (error) {
        console.error(error);
        showAlert(dispatch, 'error', 'Error al calcular el costo de emisión');
        setOriginalCalculation(null);
        setModifiedCalculation(null);
      }
    };

    performCalculations();
  }, [
    amount,
    daysDifference,
    quotedCurrency,
    type,
    rateControls,
    subProductsData,
    originalWarranty,
  ]);

  const originalPremium = originalCalculation?.amount || 0;
  const modifiedPremium = modifiedCalculation?.amount || 0;
  const premiumDifference = modifiedPremium - originalPremium;

  const originalRates = {
    variableRate: originalWarranty.operationCostVariable,
    fixedRate: originalWarranty.operationCostFixed,
    variableDiscount: originalWarranty.discountRateVariable,
    fixedDiscount: originalWarranty.discountRateFixed,
  };

  const hasRateChanges =
    rateControls.variableRate !== originalRates.variableRate ||
    rateControls.fixedRate !== originalRates.fixedRate ||
    rateControls.variableDiscount !== originalRates.variableDiscount ||
    rateControls.fixedDiscount !== originalRates.fixedDiscount;

  const originalWarrantyPremium = originalCalculation?.amount || 0;
  const totalPremiumDifference = modifiedPremium - originalWarrantyPremium;
  const hasSignificantChanges = Math.abs(totalPremiumDifference) > 1000;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogContent>
        <CustomDialogTitle setOpen={onClose}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                backgroundColor: hasRateChanges
                  ? 'warning.main'
                  : 'success.main',
                borderRadius: '50%',
                p: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Calculate sx={{ fontSize: '1rem' }} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1.1rem',
              }}
            >
              Confirmación de Edición de Garantía
            </Typography>
            <Chip
              label={hasRateChanges ? 'Tasas Modificadas' : 'Tasas Estándar'}
              size="small"
              color={hasRateChanges ? 'warning' : 'success'}
              variant="outlined"
              sx={{ height: '20px', fontSize: '0.7rem' }}
            />
          </Stack>
        </CustomDialogTitle>
        <Grid container>
          {/* Main Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <AttachMoney
                    sx={{ fontSize: '0.8rem', color: 'success.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Monto de Garantía
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {formatMoney(amount)} {quotedCurrency}
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    borderRadius: '50%',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Calculate
                    sx={{ fontSize: '0.8rem', color: 'primary.main' }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.65rem' }}
                  >
                    Costo de emisión
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ fontSize: '0.85rem' }}
                  >
                    {formatMoney(modifiedPremium)} CLP
                  </Typography>
                </Box>
              </Box>

              {hasSignificantChanges && (
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      backgroundColor:
                        totalPremiumDifference > 0
                          ? 'rgba(76, 175, 80, 0.1)'
                          : 'rgba(244, 67, 54, 0.1)',
                      borderRadius: '50%',
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {totalPremiumDifference > 0 ? (
                      <TrendingUpIcon
                        sx={{ fontSize: '0.8rem', color: 'success.main' }}
                      />
                    ) : (
                      <TrendingDownIcon
                        sx={{ fontSize: '0.8rem', color: 'error.main' }}
                      />
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.65rem' }}
                    >
                      Variación Total de Costo de Emisión
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight="medium"
                      sx={{
                        fontSize: '0.85rem',
                        color:
                          totalPremiumDifference > 0
                            ? 'success.main'
                            : 'error.main',
                      }}
                    >
                      {totalPremiumDifference > 0 ? '+' : ''}
                      {formatMoney(totalPremiumDifference)} CLP
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Rate Changes */}
          {hasRateChanges && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: '0.8rem', mb: 1 }}
                >
                  Cambios en Tasas:
                </Typography>

                {rateControls.variableRate !== originalRates.variableRate && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Tasa Variable:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.variableRate?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.variableRate ?? 0) >
                            (originalRates.variableRate ?? 0)
                              ? 'success.main'
                              : 'error.main',
                        }}
                      >
                        {rateControls.variableRate?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.fixedRate !== originalRates.fixedRate && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Tasa Fija:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.fixedRate?.toFixed(2)} UF
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.fixedRate ?? 0) >
                            (originalRates.fixedRate ?? 0)
                              ? 'success.main'
                              : 'error.main',
                        }}
                      >
                        {rateControls.fixedRate?.toFixed(2)} UF
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.variableDiscount !==
                  originalRates.variableDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Descuento Variable:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.variableDiscount?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.variableDiscount ?? 0) >
                            (originalRates.variableDiscount ?? 0)
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {rateControls.variableDiscount?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}

                {rateControls.fixedDiscount !== originalRates.fixedDiscount && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem' }}
                    >
                      Descuento Fijo:
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {originalRates.fixedDiscount?.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                        →
                      </Typography>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        sx={{
                          fontSize: '0.7rem',
                          color:
                            (rateControls.fixedDiscount ?? 0) >
                            (originalRates.fixedDiscount ?? 0)
                              ? 'error.main'
                              : 'success.main',
                        }}
                      >
                        {rateControls.fixedDiscount?.toFixed(2)}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={runDecisionEngine}
                onChange={(e) => setRunDecisionEngine(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <InfoIcon sx={{ fontSize: '1rem', color: 'info.main' }} />
                <Typography variant="body2">
                  Ejecutar motor de decisiones después de la actualización
                </Typography>
              </Box>
            }
          />
        </Box>

        {/* Alerts */}
        {hasRateChanges && (
          <AlertBanner
            title="Tasas Modificadas"
            message={`Las tasas han sido modificadas respecto a la cotización original. El costo de emisión ha variado en ${formatMoney(Math.abs(premiumDifference))} CLP (${premiumDifference > 0 ? 'incremento' : 'reducción'}).`}
            severity="warning"
          />
        )}

        {isApproved && (
          <AlertBanner
            title="Garantía Aprobada"
            message="Al editar una garantía aprobada, el documento de pago generado por el costo de emisión será eliminado automáticamente. Se creará un nuevo documento de pago con los valores actualizados."
            severity="info"
          />
        )}

        {hasSignificantChanges && (
          <AlertBanner
            title="Cambios Significativos Detectados"
            message={`Se han detectado cambios significativos en el costo de emisión (${formatMoney(Math.abs(totalPremiumDifference))} CLP). Se recomienda ejecutar el motor de decisiones para re-evaluar la garantía.`}
            severity="warning"
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'flex-end', gap: 2 }}>
        <CustomButton color="secondary" onClick={onClose}>
          Cancelar
        </CustomButton>
        <CustomButton
          color="primary"
          onClick={handleConfirm}
          loading={isPending}
          disabled={isPending}
        >
          Confirmar y Actualizar Garantía
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default WarrantyEditConfirmation;
